import {getField, updateField} from "vuex-map-fields";
import Repository from "@/repository";
import {
    issueMetrics,
    renewalMetrics,
    inspectionFailureRates,
    inspectionOutcomeMetrics,
    inspectionDailySubmissions,
    inspectionMetricWindows
} from "@/modules/dashboard/dashboardMetrics";

const SUBMISSION_TREND_DAYS = 7;

const USERSETTINGS_API = "/user/settings";
// Counts over every issue; the /issue list stops at 1000.
const ISSUE_METRICS_API = "/issue/metrics";
const CONTACT_RENEWAL_API = "/contactRenewal";
// Totals scored in SQL; the full list carries every record's data and summary
// and froze the dashboard on large accounts.
const INSPECTION_METRICS_API = "/inspectionRecord/metrics";
const SERVICE_API = "/service";
const VEHICLE_RENEWAL_METRICS_API = "/dashboard/vehicleRenewalReminder";

// ----------------------- STATE ------------------------- //
const state = {
    usersettings_data: [],
    currentIndex: -1,

    // Panel metrics. Each stays null until its source has loaded, which is what
    // the panels key their "—" placeholder off — a dashboard that has not
    // fetched yet must not show a number.
    issues: null,
    vehicleRenewals: null,
    contactRenewals: null,
    inspectionDaily: null,
    inspectionRates: null,
    inspectionOutcomes: null,
    services: null,

    // Guards a second panel on the same source from firing a duplicate fetch;
    // several panels share one endpoint and all mount at once.
    loading: {},
};

// ----------------------- GETTERS ----------------------- //
const getters = {
    getUserSettings: (state) =>
        getField(state.usersettings_data[state.currentIndex]),

    getCurrentUserSettings: (state) => {
        return state.usersettings_data[state.currentIndex];
    },

    // Issues. The model has no severity field and no "pending" state, so the
    // only honest split is open/closed and the overdue subset of the open ones.
    getOpenIssues: (state) => (state.issues ? state.issues.open : null),
    getClosedIssues: (state) => (state.issues ? state.issues.closed : null),
    getOverdueIssues: (state) => (state.issues ? state.issues.overdue : null),
    getOverdueIssuePercent: (state) => (state.issues ? state.issues.overduePercent : null),
    getVehiclesWithOpenIssues: (state) => (state.issues ? state.issues.vehiclesAffected : null),

    // Renewals.
    getVehicleRenewalOverDue: (state) => (state.vehicleRenewals ? state.vehicleRenewals.overdue : null),
    getVehicleRenewalDueSoon: (state) => (state.vehicleRenewals ? state.vehicleRenewals.due_soon : null),
    getContactRenewalOverDue: (state) => (state.contactRenewals ? state.contactRenewals.overdue : null),
    getContactRenewalDueSoon: (state) => (state.contactRenewals ? state.contactRenewals.due_soon : null),

    // Inspections.
    getCurrentWeekfailureRate: (state) => (state.inspectionRates ? state.inspectionRates.currentWeek : null),
    getLastWeekfailureRate: (state) => (state.inspectionRates ? state.inspectionRates.lastWeek : null),
    getFailingInspections: (state) => (state.inspectionOutcomes ? state.inspectionOutcomes.failing : null),
    getFailingInspectionPercent: (state) => (state.inspectionOutcomes ? state.inspectionOutcomes.failingPercent : null),
    getCleanInspections: (state) => (state.inspectionOutcomes ? state.inspectionOutcomes.clean : null),

    // Derived on read rather than stored, so the daily trend and the headline
    // counts can never describe different fetches.
    getInspectionDailySubmissions: (state) =>
        state.inspectionDaily ? inspectionDailySubmissions(state.inspectionDaily, SUBMISSION_TREND_DAYS) : null,
};

// ----------------------- MUTATIONS -------------------- //
const mutations = {
    updateUserSettings(state, payload) {
        updateField(state.usersettings_data[state.currentIndex], payload);
    },

    setCurrentIndexByID(state, theID) {
        state.currentIndex = state.usersettings_data.findIndex(
            (usersettings) => usersettings.id == theID
        );
    },

    loadAllUserSettingss(state, payload) {
        state.usersettings_data = payload.data;
        state.currentIndex = 0;
    },

    addUserSettings(state, payload) {
        state.usersettings_data.push(payload.data);
        state.currentIndex = state.usersettings_data.length - 1;
    },



    saveUserSettings(state, payload) {
        state.usersettings_data[state.currentIndex] = payload.data;
    },

    fetchUserSettings(state, payload) {
        if (state.currentIndex == -1) {
            state.usersettings_data.push(payload.data);
            state.currentIndex = 0;
        } else {
            state.usersettings_data[state.currentIndex] = payload.data;
        }
    },

    deleteUserSettings(state, payload) {
        state.currentIndex = state.usersettings_data.findIndex(
            (usersettings) => usersettings.id == payload.data.id
        );
        state.usersettings_data.splice(state.currentIndex, 1);
    },



    setMetricsLoading(state, key) {
        state.loading = Object.assign({}, state.loading, {[key]: true});
    },

    clearMetricsLoading(state, key) {
        state.loading = Object.assign({}, state.loading, {[key]: false});
    },

    setIssueMetrics(state, payload) {
        state.issues = issueMetrics(payload.data);
    },

    setVehicleRenewalMetrics(state, payload) {
        state.vehicleRenewals = payload.data;
    },

    setContactRenewalMetrics(state, payload) {
        state.contactRenewals = renewalMetrics(payload.data);
    },

    setInspectionMetrics(state, payload) {
        state.inspectionDaily = payload.data.daily;
        state.inspectionRates = inspectionFailureRates(payload.data);
        state.inspectionOutcomes = inspectionOutcomeMetrics(payload.data);
    },

    setServices(state, payload) {
        state.services = payload.data;
    },

    reset(state) {
        state.usersettings_data = []
        state.currentIndex = -1

        state.issues = null
        state.vehicleRenewals = null
        state.contactRenewals = null
        state.inspectionDaily = null
        state.inspectionRates = null
        state.inspectionOutcomes = null
        state.services = null
        state.loading = {}
    }
};

// ----------------------- ACTIONS -------------------- //

// Panels mount together and several share a source, so every metric loader runs
// through this: it skips the fetch when the data is already in state or a
// request for the same key is in flight.
async function loadMetric({state, commit}, {key, url, mutation, force}) {
    if (state.loading[key]) return;
    if (!force && state[key] !== null) return;

    commit("setMetricsLoading", key);
    try {
        commit(mutation, await Repository.get(url));
    } catch (error) {
        console.warn(`dashboard: failed to load ${key} metrics`, error);
    } finally {
        commit("clearMetricsLoading", key);
    }
}

const actions = {

    async loadIssueMetrics(context, payload) {
        await loadMetric(context, {
            key: "issues",
            url: ISSUE_METRICS_API,
            mutation: "setIssueMetrics",
            force: payload && payload.force
        });
    },

    async loadVehicleRenewalMetrics(context, payload) {
        await loadMetric(context, {
            key: "vehicleRenewals",
            url: VEHICLE_RENEWAL_METRICS_API,
            mutation: "setVehicleRenewalMetrics",
            force: payload && payload.force
        });
    },

    async loadContactRenewalMetrics(context, payload) {
        await loadMetric(context, {
            key: "contactRenewals",
            url: CONTACT_RENEWAL_API,
            mutation: "setContactRenewalMetrics",
            force: payload && payload.force
        });
    },

    // One fetch feeds both the weekly failure rate and the outcome split, so
    // the two panels can never disagree about the same set of records.
    async loadInspectionMetrics(context, payload) {
        await loadMetric(context, {
            key: "inspectionRates",
            url: `${INSPECTION_METRICS_API}?${new URLSearchParams(inspectionMetricWindows(SUBMISSION_TREND_DAYS))}`,
            mutation: "setInspectionMetrics",
            force: payload && payload.force
        });
    },

    async loadServices(context, payload) {
        await loadMetric(context, {
            key: "services",
            url: SERVICE_API,
            mutation: "setServices",
            force: payload && payload.force
        });
    },

    async loadAllUserSettingss({commit}) {
        try {
            commit(
                "loadAllUserSettingss",
                await Repository.get(`${USERSETTINGS_API}`)
            );
        } catch (error) {
            console.log(error);
        }
    },

    async addUserSettings({commit}) {
        await Repository.get(`${USERSETTINGS_API}/new`)
            .then((response) => {
                commit("addUserSettings", response);
            })
            .catch((error) => {
                console.log(error);
            });
    },

    async saveUserSettings({state, commit}) {
        try {
            commit(
                "saveUserSettings",
                await Repository.post(
                    `${USERSETTINGS_API}`,
                    state.usersettings_data[state.currentIndex]
                )
            );
        } catch (error) {
            console.log(error);
        }
    },

    async fetchUserSettings({commit}, payloadID) {
        try {
            commit(
                "fetchUserSettings",
                await Repository.get(`${USERSETTINGS_API}/${payloadID}`)
            );
        } catch (error) {
            console.log(error);
        }
    },

    async deleteUserSettings({commit}, payloadID) {
        try {
            commit(
                "deleteUserSettings",
                await Repository.delete(`${USERSETTINGS_API}/${payloadID}`)
            );
        } catch (error) {
            console.log(error);
        }
    },

    async searchUserSettingss({commit}, searchTerm) {
        try {
            commit(
                "loadAllUserSettingss",
                await Repository.get(`${USERSETTINGS_API}/search?q=${searchTerm}`)
            );
        } catch (error) {
            console.log(error);
        }
    },
};
export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
